import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import GoogleButton from "@/components/xuma/GoogleButton";
import Logo from "@/components/xuma/Logo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("¡Bienvenido de nuevo!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-xuma-bg grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-xuma-dark text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-[0.07]" />
        <Link to="/" className="flex items-center relative">
          <Logo variant="light" size="md" />
        </Link>
        <div className="relative">
          <h2 className="font-heading font-extrabold text-4xl leading-tight">Tu negocio en una tarjeta que trabaja por ti</h2>
          <p className="mt-4 text-white/60 max-w-sm">Presenta tus servicios, recibe contactos y organiza oportunidades desde un solo lugar.</p>
        </div>
        <p className="font-heading text-lg text-white/80 relative">Cada contacto suma.</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-xuma-muted hover:text-xuma-dark mb-8">
            <ArrowLeft className="h-4 w-4" /> Volver
          </Link>
          <h1 className="font-heading font-extrabold text-3xl text-xuma-dark">Entrar</h1>
          <p className="mt-2 text-xuma-muted">Accede a tu panel de Xuma Social Business Card.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Correo</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.com" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5 h-11" />
            </div>
            <Button type="submit" data-testid="login-submit" disabled={loading}
              className="w-full h-11 rounded-full bg-xuma-primary hover:bg-xuma-primaryHover text-white">
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-xuma-muted">
            <span className="h-px flex-1 bg-xuma-border" /> o <span className="h-px flex-1 bg-xuma-border" />
          </div>
          <GoogleButton label="Entrar con Google" />
          <p className="mt-3 text-[11px] leading-relaxed text-center text-xuma-muted">
            Al continuar con Google, aceptas los{" "}
            <Link to="/legal/terminos-y-condiciones" target="_blank" className="text-xuma-primary hover:underline">Términos</Link>{" "}
            y el{" "}
            <Link to="/legal/aviso-de-privacidad" target="_blank" className="text-xuma-primary hover:underline">Aviso de Privacidad</Link>.
          </p>

          <p className="mt-8 text-sm text-center text-xuma-muted">
            ¿No tienes cuenta?{" "}
            <Link to="/register" className="text-xuma-primary font-medium hover:underline">Crear cuenta</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
